import { z } from "zod";
import type { Json, Tables } from "./database";

const filterOptionListSchema = z.array(z.string()).default([]);

/** `get_spyserp_filter_options` RPC payload (`Returns: Json`). */
export const spyserpFilterOptionsSchema = z.object({
  keywords: filterOptionListSchema,
  domains: filterOptionListSchema,
  locations: filterOptionListSchema,
  languages: filterOptionListSchema,
});

export type SpyserpFilterOptions = z.infer<typeof spyserpFilterOptionsSchema>;

export const EMPTY_SPYSERP_FILTER_OPTIONS: SpyserpFilterOptions = {
  keywords: [],
  domains: [],
  locations: [],
  languages: [],
};

export const spyserpGlobalAlertsSchema = z.object({
  dropped_top_50: z.number(),
  entered_top_10: z.number(),
  dropped_top_10: z.number(),
});

export type SpyserpGlobalAlerts = z.infer<typeof spyserpGlobalAlertsSchema>;

export const EMPTY_SPYSERP_GLOBAL_ALERTS: SpyserpGlobalAlerts = {
  dropped_top_50: 0,
  entered_top_10: 0,
  dropped_top_10: 0,
};

export interface SpyserpDomainStat {
  domain: string;
  site_type: string | null;
  total_kw: number;
  top_3: number;
  top_3_percent: number;
  top_10: number;
  top_10_percent: number;
  top_20: number;
  top_20_percent: number;
  current_avg_rank: number;
  rank_change: number;
}

export interface SpyserpDomainStatsResult {
  stats: SpyserpDomainStat[];
  totalCount: number;
}

export interface SpyserpResolvedDateRange {
  startDate: string;
  endDate: string;
}

export interface SpyserpUrlStat {
  keyword: string;
  page_url: string;
  domain: string;
  site_type: string | null;
  location: string;
  lang_page: string;
  current_rank: number;
  rank_change: number;
}

export interface SpyserpUrlStatsResult {
  stats: SpyserpUrlStat[];
  totalCount: number;
}

export const spyserpInsightsSchema = z.object({
  overview: z.object({
    total_kw: z.number(),
    top_10: z.number(),
    dropped_50: z.number(),
    growing: z.number(),
    declining: z.number(),
    new_kw: z.number(),
  }),
  winners: z.array(
    z.object({
      domain: z.string(),
      kw_count: z.number(),
      total_change: z.number(),
    }),
  ),
  losers: z.array(
    z.object({
      domain: z.string(),
      kw_count: z.number(),
      total_change: z.number(),
    }),
  ),
});

export type SpyserpInsightsData = z.infer<typeof spyserpInsightsSchema>;

const segmentKeywordRuleSchema = z.object({
  match: z.string(),
  value: z.string(),
});

export const segmentRulesSchema = z.object({
  keywords: z.array(segmentKeywordRuleSchema).optional(),
  tlds: z.array(z.string()).optional(),
  locales: z.array(z.string()).optional(),
});

export type SegmentRule = z.infer<typeof segmentRulesSchema>;

export type SpyserpSegmentRow = Tables<"spyserp_segments">;

/** UI model for segment editor (non-null color, parsed rules). */
export interface Segment {
  id: string;
  name: string;
  color: string;
  target_type: "domain" | "url";
  rules: SegmentRule;
}

export const mapSpyserpSegmentRow = (row: SpyserpSegmentRow): Segment => {
  const rulesResult = segmentRulesSchema.safeParse(row.rules);
  const targetType = row.target_type === "url" ? "url" : "domain";

  return {
    id: row.id,
    name: row.name,
    color: row.color ?? "blue",
    target_type: targetType,
    rules: rulesResult.success ? rulesResult.data : {},
  };
};

export const segmentRulesToJson = (rules: SegmentRule): Json =>
  JSON.parse(JSON.stringify(rules)) as Json;
